import { Okhsl } from "../../d3-mapp/color/okhsl.js";
import { color2bin } from "../../d3-mapp/color/color.js";
export const WindVectorFactory = (color, fontSize = 12, maxChars = 4, cellSize = 100, maxArrow = 1.0, //between 0.0 and 1.0; 0.5 or lower means inside the cell; 0 always minimum
windLow = 1.0, windHigh = 10.0, lightnessTrackLength = 0.5) => {
    const _color = new Okhsl(color);
    const baseLightness = _color.l;
    const lightnessMin = baseLightness - lightnessTrackLength / 2;
    const lightBGcolor = new Okhsl("#ffffff");
    const darkBGColor = new Okhsl("#000000");
    const lightBGColorHex = lightBGcolor.formatHex8();
    const darkBGColorHex = darkBGColor.formatHex8();
    const charsToLength = (n) => n * fontSize * 0.6;
    const diameterMin = charsToLength(2);
    const diameterMax = charsToLength(maxChars + 1);
    const strokeWidth = fontSize / 4;
    const labelDY = 0.4 * fontSize;
    let dws = undefined;
    let diameter, r0, r0_2, minArrowLength, longestArrow, maxArrowLength;
    const setDisplayWindSpeed = (displayWindSpeed) => {
        if (dws === undefined || displayWindSpeed != dws) {
            //if (dws === undefined || displayWindSpeed != dws) {
            diameter = displayWindSpeed ? diameterMax : diameterMin;
            r0 = diameter / 2;
            r0_2 = (diameter * diameter) / 4;
            minArrowLength = strokeWidth * 2 + (diameterMax - diameter) / 2;
            longestArrow =
                cellSize - diameterMax - fontSize / 2 + (diameterMax - diameter) / 2;
            maxArrowLength =
                (longestArrow - minArrowLength) *
                    Math.max(Math.min(1.0, maxArrow), 0.0) +
                    minArrowLength;
        }
    };
    const lightness = (ws) => {
        return (((Math.max(ws, windLow) - windLow) * lightnessTrackLength) /
            (windHigh - windLow) +
            lightnessMin);
    };
    setDisplayWindSpeed(false);
    const factory = {
        displayWindSpeed: setDisplayWindSpeed,
        //https://www.youtube.com/watch?v=QVk3sKxshDs
        //http://tgbasics.weebly.com/tangent-to-a-point-outside-a-circle.html
        //https://stackoverflow.com/questions/3349125/circle-circle-intersection-points
        arrowPathCoordinates: (ws, angle, p) => {
            const length = ((Math.min(Math.max(ws, windLow), windHigh) - windLow) *
                (maxArrowLength - minArrowLength)) /
                (windHigh - windLow) +
                minArrowLength;
            const p0 = p;
            const d = length + r0;
            const d_2 = d * d;
            const r1_2 = d_2 - r0_2;
            const r1 = Math.sqrt(r1_2);
            const p1 = { x: Math.cos(angle) * d + p.x, y: Math.sin(angle) * d + p.y };
            const p1minusp0 = { x: p1.x - p0.x, y: p1.y - p0.y };
            const a = (r0_2 - r1_2 + d_2) / (2 * d);
            const h = Math.sqrt(r0_2 - a * a);
            const p2 = {
                x: p0.x + (a * p1minusp0.x) / d,
                y: p0.y + (a * p1minusp0.y) / d,
            };
            const p3a = {
                x: p2.x + (h * p1minusp0.y) / d,
                y: p2.y - (h * p1minusp0.x) / d,
            };
            const p3b = {
                x: p2.x - (h * p1minusp0.y) / d,
                y: p2.y + (h * p1minusp0.x) / d,
            };
            return [p3a.x, p3a.y, p1.x, p1.y, p3b.x, p3b.y];
        },
        arrowPath: (ws, angle, p) => {
            const coords = factory.arrowPathCoordinates(ws, angle, p);
            return `M ${coords[0]} ${-coords[1]} L ${coords[2]} ${-coords[3]} L ${coords[4]} ${-coords[5]} Z`;
        },
        radius: () => (diameter - strokeWidth) / 2,
        stroke: () => strokeWidth,
        label: (ws) => (ws > 0 ? ws : 0).toFixed(ws > 100 ? 0 : ws >= 1.0 ? 1 : 2),
        color: (ws) => {
            _color.l = lightness(ws);
            return _color.formatHex();
        },
        colorAsArray: (ws, buf, byteOffset) => {
            _color.l = lightness(ws);
            return color2bin(_color, buf, byteOffset);
        },
        innerColor: (ws, displayWindSpeed) => displayWindSpeed
            ? lightness(ws) < 0.5 && displayWindSpeed
                ? lightBGColorHex
                : darkBGColorHex
            : factory.color(ws),
        innerColorAsArray: (ws, displayWindSpeed, buf, byteOffset) => {
            if (displayWindSpeed) {
                color2bin(lightness(ws) < 0.5 ? lightBGcolor : darkBGColor, buf, byteOffset);
            }
            else {
                _color.l = lightness(ws);
                return color2bin(_color, buf, byteOffset);
            }
        },
        fontSize: () => fontSize,
        labelDY: () => labelDY,
        diameterNin: () => diameterMin,
        diameterMax: () => diameterMax,
        radiusMin: () => (diameterMin - strokeWidth) / 2,
        radiusMax: () => (diameterMax - strokeWidth) / 2,
        cellSize: () => cellSize,
        steps: (u, size) => { },
        positions: (nx, ny, p) => {
            const positions = new Float32Array(nx.length * ny.length * 2);
            let i = 0;
            ny.forEach((iy) => {
                nx.forEach((ix) => {
                    const position = p(ix, iy);
                    positions[i++] = position.x;
                    positions[i++] = position.y;
                });
            });
            return positions;
        },
    };
    return factory;
};
